import { FC, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { ProviderHubContext } from "@app/projects/network-assessment/provider-hub/contexts/ProviderHubContext";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { ContractEntityProvidersFilters, ContractEntityProvidersFiltersRequestModel, FiltersAction, ProviderProfileActionEnum } from "@app/projects/network-assessment/provider-hub/interfaces";
import { ProviderActionStatusEnum } from "@app/projects/enumerators";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { NewActionMenu } from "@app/shared/action-menu";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { UserContext } from "@app/user/UserContext";
import HospitalProfileActionFlyout from "@app/projects/network-assessment/provider-hub/hospital-profile/HospitalProfileActionFlyout";
import { ProviderProfileRouteState } from "@app/projects/network-assessment/provider-hub/provider-profile/interfaces/providerProfileRouteState";
import { Breadcrumb } from "@app/shared/breadcrumbs/breadcrumb";
import { FlyoutLink } from "@app/shared/Flyouts";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { QESButton, QESButtonTypeClasses } from "@app/shared/qes-standards/Button";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { CheckboxStateEnum } from "@app/shared/qes-standards/forms/Checkbox";
import { NestingParentDropdownItem, DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { ContractEntityFilters } from "@app/projects/network-assessment/provider-hub/contract-entity-profile/ContractEntityFilters";
import { ProviderHubSpecialty } from "@app/projects/network-assessment/provider-hub/contract-entity-profile/ProviderHubSpecialtyView";
import { HospitalProfileKeyPoints } from "@app/projects/network-assessment/provider-hub/hospital-profile/HospitalProfileKeyPoints";
import { HospitalProfileOverview } from "@app/projects/network-assessment/provider-hub/hospital-profile/HospitalProfileOverview";
import { HospitalProfileEntityBreakdown } from "@app/projects/network-assessment/provider-hub/hospital-profile/HospitalProfileEntityBreakdown";
import { HospitalProfileProviders } from "@app/projects/network-assessment/provider-hub/hospital-profile/HospitalProfileProviders";
import HospitalServicesTable from "@app/projects/network-assessment/provider-hub/hospital-profile/HospitalServicesTable";
import { HospitalIapDrawer } from "@app/projects/network-assessment/provider-hub/hospital-profile/HospitalIapDrawer";
import { HospitalIapReportButton } from "@app/projects/network-assessment/provider-hub/hospital-profile/HospitalIapReportButton";
import ProviderHubService from "@app/projects/network-assessment/provider-hub/providerHubService";

export const ProviderHubHospitalProfile: FC = () => {
  const location = useLocation<ProviderProfileRouteState>();
  const projectContext = useContext(CountyProjectContext);
  const providerHubContext = useContext(ProviderHubContext);
  const { selectedHospital } = providerHubContext;
  const featureFlagContext = useContext(FeatureFlagContext);
  const userContext = useContext(UserContext);
  const providerHubService = useRef(new ProviderHubService()).current;
  const rightColumnRef = useRef<HTMLDivElement>(null);

  const {
    data: accessPoints = [],
    isFetching: isLoadingAccessPoints,
    refetch: refetchAccessPoints,
  } = useQuery({
    enabled: !!selectedHospital,
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) => providerHubService.getAccessPointsByHospital(
      projectContext.project.projectId,
      selectedHospital!.projectRunConfigurationId,
      selectedHospital!.ccn,
      signal),
    queryKey: ["provider-hub-hospital-access-points", selectedHospital?.projectRunConfigurationId, selectedHospital?.ccn],
  });

  const outOfNetworkTypes = useRef<ProviderNetworkTypeEnum[]>([
    ProviderNetworkTypeEnum.Prospect,
    ProviderNetworkTypeEnum.Market,
    ProviderNetworkTypeEnum.NetNew,
  ]).current;

  const outOfNetworkAccessPoints = useMemo(
    () => accessPoints.filter(ap => outOfNetworkTypes.includes(ap.providerNetworkType)),
    [accessPoints, outOfNetworkTypes]
  );

  const networkAccessPoints = useMemo(
    () => accessPoints.filter(ap => ap.providerNetworkType === ProviderNetworkTypeEnum.Network),
    [accessPoints]
  );

  const accessPointsForExclusion = useMemo(
    () => outOfNetworkAccessPoints.filter(ap => ap.providerActionStatus !== ProviderActionStatusEnum.FullyExcluded),
    [outOfNetworkAccessPoints]
  );

  const accessPointsForTargeting = useMemo(
    () => outOfNetworkAccessPoints.filter(ap => ap.providerActionStatus !== ProviderActionStatusEnum.FullyTargeted),
    [outOfNetworkAccessPoints]
  );

  const accessPointsForTermination = useMemo(
    () => networkAccessPoints.filter(ap => ap.providerActionStatus !== ProviderActionStatusEnum.FullyTerminated),
    [networkAccessPoints]
  );

  const hospitalIsExcluded = useMemo(
    () => outOfNetworkAccessPoints.length > 0 && outOfNetworkAccessPoints.every(ap => ap.providerActionStatus === ProviderActionStatusEnum.FullyExcluded),
    [outOfNetworkAccessPoints]
  );

  const hasAvailableActions = useMemo(() => {
    const hasExcludeRole = userContext.roles.includes(Roles.PROVIDER_EXCLUDE_USER);
    const hasTargetRole = userContext.roles.includes(Roles.PROVIDER_TARGET_USER);
    const hasTerminateRole = userContext.roles.includes(Roles.TERM_PROVIDER_ADMIN);
    if (!hasExcludeRole && !hasTargetRole && !hasTerminateRole) return false;
    return (hasExcludeRole && outOfNetworkAccessPoints.length > 0)
      || (hasTargetRole && outOfNetworkAccessPoints.length > 0)
      || (hasTerminateRole && networkAccessPoints.length > 0);
  }, [outOfNetworkAccessPoints, networkAccessPoints, userContext.roles]);

  const [pageIndex, setPageIndex] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(50);
  const [resetToFirstPage, setResetToFirstPage] = useState<boolean>(false);
  const [specialtyOptions, setSpecialtyOptions] = useState<DropdownItem<string>[]>([]);
  const { activeServiceAreaStatusFilters, serviceAreaMatchFilter, serviceAreaOptions, serviceAreaStatusFilters, setActiveServiceAreaStatusFilters, setServiceAreaOptions } = providerHubContext;

  /* oxlint-disable sort-keys */
  const providerNetworkTypeOptions: NestingParentDropdownItem[] = [
    { key: ProviderNetworkTypeEnum.Network, checkboxStatus: CheckboxStateEnum.Unchecked, name: 'In-Network', children: [] },
    {
      key: 'Out-of-Network', name: 'Out-of-Network', checkboxStatus: CheckboxStateEnum.Unchecked, children: [
        { key: ProviderNetworkTypeEnum.Prospect, name: 'Prospect' },
        { key: ProviderNetworkTypeEnum.Market, name: 'Market' },
        { key: ProviderNetworkTypeEnum.NetNew, name: 'Net New' }
      ]
    }
  ];
  /* oxlint-enable sort-keys */
  const [defaultProviderNetworkTypes] = useState<ProviderNetworkTypeEnum[]>(
    [
      ProviderNetworkTypeEnum.Network,
      ...providerNetworkTypeOptions.flatMap(pnt => pnt.children).map(c => c.key as ProviderNetworkTypeEnum)
    ]
  );

  // Go Back is now browser Back (the URL is the source of truth for which profile shows); this wrapper just
  // resets the breadcrumb before popping the history entry.
  const handleGoBack = () => {
    providerHubContext.setErrorLoadingHospital(false);
    projectContext.setBreadcrumbItems([projectContext.project.resource.name]);
    providerHubContext.goBackFromProfile();
  };

  useEffect(() => {
    const productUrl = location.pathname.split("/").slice(0, 3).join("/") + "/adequacy";
    const middleCrumb: Breadcrumb = {
      onClick: handleGoBack,
      text: "Hospital",
    };

    projectContext.setBreadcrumbItems([
      {
        link: productUrl,
        text: projectContext.project.resource.name,
      },
      middleCrumb,
      {
        text: selectedHospital?.hospitalName ?? "Loading",
      }
    ]);
  }, [projectContext.project.resource.name, providerHubContext.selectedHospital]);

  const initialFilterState: ContractEntityProvidersFilters = {
    pageIndex: 0,
    pageSize: 50,
    providerNetworkTypes: providerHubContext.filterState.providerNetworkTypes ?? [],
    regionSetIds: providerHubContext.filterState.regionSetIds ?? [],
    specialtyGroupCodes: providerHubContext.filterState.specialtyGroupCodes ?? [],
  }

  const filtersReducer = (filtersState: ContractEntityProvidersFilters, action: FiltersAction): ContractEntityProvidersFilters => {
    switch (action.type) {
      case 'SET_FILTER':
        return {
          ...filtersState,
          [action.key]: action.value,
        };
      case 'RESET_FILTERS':
        return initialFilterState;
      default:
        return initialFilterState;
    }
  };

  const [filterState, filterStateDispatch] = useReducer(filtersReducer, initialFilterState);
  const [selectedProviderNetworkTypes, setSelectedProviderNetworkTypes] = useState<ProviderNetworkTypeEnum[]>(filterState.providerNetworkTypes.length === 0 ? defaultProviderNetworkTypes : filterState.providerNetworkTypes);

  const setFilter = <T extends keyof ContractEntityProvidersFilters>(key: T, value: ContractEntityProvidersFilters[T]) => {
    filterStateDispatch({ key, type: 'SET_FILTER', value });
  };

  // Filters are applied to the query only when the user clicks Search (or resets), matching the existing
  // explicit-search behavior. Initial applied filters come from the search-results context (folded into
  // initialFilterState above), so no mount-time filter-restore effect is needed.
  const [appliedFilters, setAppliedFilters] = useState<ContractEntityProvidersFilters>(initialFilterState);

  const buildFilterRequestModel = (filters: ContractEntityProvidersFilters): ContractEntityProvidersFiltersRequestModel => ({
    pageIndex,
    pageSize,
    providerNetworkTypes: filters.providerNetworkTypes,
    regionSetIds: filters.regionSetIds.map(Number),
    specialtyGroupCodes: filters.specialtyGroupCodes.map(item => item.key),
  });

  const { data: hospitalServicesData = { items: [], totalCount: 0 } } = useQuery({
    enabled: !!selectedHospital,
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) => providerHubService.getHospitalServices(
      pageIndex, pageSize, projectContext?.project?.projectId,
      selectedHospital!.projectRunConfigurationId, selectedHospital!.ccn,
      buildFilterRequestModel(appliedFilters), signal),
    queryKey: ["provider-hub-hospital-services", selectedHospital?.projectRunConfigurationId, selectedHospital?.ccn, pageIndex, pageSize, JSON.stringify(appliedFilters)],
  });

  useEffect(() => {
    rightColumnRef.current?.querySelector('.waffles-container')?.scrollTo(0, 0);
  }, [pageIndex]);

  const handleResetAll = () => {
    filterStateDispatch({ type: 'RESET_FILTERS' });
    setFilter('providerNetworkTypes', []);
    setSelectedProviderNetworkTypes([]);
    setFilter('specialtyGroupCodes', []);
    setFilter('regionSetIds', []);
    setActiveServiceAreaStatusFilters(new Set());

    providerHubContext.setFilter('specialtyGroupCodes', []);
    providerHubContext.setFilter('providerNetworkTypes', []);
    providerHubContext.setFilter('regionSetIds', []);

    // Commit the cleared filters to the query and jump back to page 1.
    setAppliedFilters({ pageIndex: 0, pageSize, providerNetworkTypes: [], regionSetIds: [], specialtyGroupCodes: [] });
    setPageIndex(1);
    setResetToFirstPage(true);
  }

  const handleSearchClick = () => {
    // Commit the edited filters to the query key; keepPreviousData avoids a flicker while refetching.
    setAppliedFilters(filterState);
    setPageIndex(1);
    setResetToFirstPage(true);
  }

  if (!selectedHospital && !providerHubContext.errorLoadingHospital) {
    return <QesIcon iconName={QesIcons.LoadingV1} className="qes-icon-spin" description="loading Hospital" />;
  } else if (providerHubContext.errorLoadingHospital) {
    return <QESAlert type="danger" persist={true}>We did not find the requested Hospital.</QESAlert>;
  }

  return (
    <div className="contractentity-profile__grid">
      <div className="contractentity-profile__top-row">
        <div className="contractentity-profile__top-row-buttons">
          <QESButton
            qesType="outline-inherit"
            qesTestId="provider-hub-hospital-detail__back-to-results"
            onClick={handleGoBack}
            className="provider-profile__back-to-results"
          >
            Go Back
          </QESButton>
          <div className="hospital-profile__top-row-actions">
            <FlyoutLink
              asDrawer={true}
              classNames={QESButtonTypeClasses.get("outline")}
              content={{
                body: <HospitalIapDrawer />,
                title: "Institutionally Affiliated Providers",
                topActionItems: [<HospitalIapReportButton key="generate-report" />],
              }}
              size="90"
              testId="hospital-profile__iap-drawer-link">
              View Institutionally-affiliated Practitioners
            </FlyoutLink>
            {(!providerHubContext.isScenarioModelingProcessing &&
              !providerHubContext.isScenarioModelingError &&
              featureFlagContext.enabledFeatureFlags.has(Features.PROVIDER_HUB_PROJECTION) &&
              (hasAvailableActions || isLoadingAccessPoints)) &&
              <RoleRestricted allowedRoles={[Roles.PROVIDER_EXCLUDE_USER, Roles.PROVIDER_TARGET_USER, Roles.TERM_PROVIDER_ADMIN]}>
                <div className="provider-profile__provider-actions-info">
                  <div>
                    <TooltipWrapper text="Actions associated with Hospitals only affect services, not institutionally-affiliated providers" position="bottom">
                      <QesIcon iconName={QesIcons.InfoCircle} description="Tooltip" />
                    </TooltipWrapper>
                  </div>
                  <NewActionMenu
                    alignRight={true}
                    buttonClassNames="qes-button-primary"
                    buttonText="Hospital Actions"
                    containerClassnames="provider-profile__provider-actions-menu"
                    testId="hospital-actions-select"
                  >
                    {outOfNetworkAccessPoints.length > 0 &&
                      <RoleRestricted allowedRoles={[Roles.PROVIDER_EXCLUDE_USER]}>
                        <FlyoutLink
                          content={{
                            body: <HospitalProfileActionFlyout
                              accessPoints={accessPointsForExclusion.length > 0 ? accessPointsForExclusion : outOfNetworkAccessPoints}
                              onSubmit={refetchAccessPoints}
                              providerAction={ProviderProfileActionEnum.Exclude}
                              targetedAccessPointsCount={outOfNetworkAccessPoints.length - accessPointsForTargeting.length}
                            />,
                            title: "Exclude Hospital"
                          }}
                          classNames="action-menu-item col-12 fw-normal"
                          disabled={accessPointsForExclusion.length === 0}
                          size="60"
                          testId="hospital-profile__exclude-flyout"
                        >
                          {accessPointsForExclusion.length === 0 ? (
                            <TooltipWrapper text="This hospital is fully excluded across all applicable access points" position="bottom">
                              Exclude
                            </TooltipWrapper>
                          ) : (
                            "Exclude"
                          )}
                        </FlyoutLink>
                      </RoleRestricted>
                    }
                    {outOfNetworkAccessPoints.length > 0 &&
                      <RoleRestricted allowedRoles={[Roles.PROVIDER_TARGET_USER]}>
                        <FlyoutLink
                          content={{
                            body: <HospitalProfileActionFlyout
                              accessPoints={accessPointsForTargeting.length > 0 ? accessPointsForTargeting : outOfNetworkAccessPoints}
                              excludedAccessPointsCount={outOfNetworkAccessPoints.length - accessPointsForExclusion.length}
                              onSubmit={refetchAccessPoints}
                              providerAction={ProviderProfileActionEnum.Target}
                            />,
                            title: "Target Hospital"
                          }}
                          classNames="action-menu-item col-12 fw-normal"
                          disabled={accessPointsForTargeting.length === 0 || hospitalIsExcluded}
                          size="40"
                          testId="hospital-profile__target-flyout"
                        >
                          {accessPointsForTargeting.length === 0 ? (
                            <TooltipWrapper text="This hospital is fully targeted across all applicable access points" position="bottom">
                              Target
                            </TooltipWrapper>
                          ) : hospitalIsExcluded ? (
                            <TooltipWrapper text="This hospital is fully excluded across all applicable access points. To target, remove the Exclusion." position="bottom">
                              Target
                            </TooltipWrapper>
                          ) : (
                            "Target"
                          )}
                        </FlyoutLink>
                      </RoleRestricted>
                    }
                    {networkAccessPoints.length > 0 &&
                      <RoleRestricted allowedRoles={[Roles.TERM_PROVIDER_ADMIN]}>
                        <FlyoutLink
                          content={{
                            body: <HospitalProfileActionFlyout
                              accessPoints={accessPointsForTermination}
                              onSubmit={refetchAccessPoints}
                              providerAction={ProviderProfileActionEnum.Terminate}
                            />,
                            title: "Terminate Hospital"
                          }}
                          classNames="action-menu-item col-12 fw-normal"
                          disabled={accessPointsForTermination.length === 0}
                          size="40"
                          testId="hospital-profile__terminate-flyout"
                        >
                          {accessPointsForTermination.length === 0 ? (
                            <TooltipWrapper text="This hospital is fully terminated across all applicable access points" position="bottom">
                              Terminate
                            </TooltipWrapper>
                          ) : (
                            "Terminate"
                          )}
                        </FlyoutLink>
                      </RoleRestricted>
                    }
                  </NewActionMenu>
                </div>
              </RoleRestricted>
            }
          </div>
        </div>
      </div>
      <h1 className="organization-profile__names">
        <div className="organization-profile__name">{selectedHospital.hospitalName}</div>
      </h1>
      <div className="organization-profile__filters-row">
        <ContractEntityFilters
          activeServiceAreaStatusFilters={activeServiceAreaStatusFilters}
          filterProviderHubState={providerHubContext.filterState}
          filterState={filterState}
          handleResetAll={handleResetAll}
          handleSearchClick={handleSearchClick}
          providerNetworkTypeOptions={providerNetworkTypeOptions}
          selectedProviderNetworkTypes={selectedProviderNetworkTypes}
          serviceAreaMatchFilter={serviceAreaMatchFilter}
          serviceAreaOptions={serviceAreaOptions}
          serviceAreaStatusFilters={serviceAreaStatusFilters}
          setActiveServiceAreaStatusFilters={setActiveServiceAreaStatusFilters}
          setFilter={setFilter}
          setProviderHubFilter={providerHubContext.setFilter}
          setSelectedProviderNetworkTypes={setSelectedProviderNetworkTypes}
          setServiceAreaOptions={setServiceAreaOptions}
          setSpecialtyOptions={setSpecialtyOptions}
          specialtyOptions={specialtyOptions}
        />
      </div>

      {/* First Column */}
      <div className="contractentity-profile__grid-column--scroll">
        <HospitalProfileKeyPoints />
        <AccordionContainer>
          <HospitalProfileOverview />
        </AccordionContainer>
        <AccordionContainer>
          <HospitalProfileEntityBreakdown />
        </AccordionContainer>
        <AccordionContainer>
          <HospitalProfileProviders />
        </AccordionContainer>
        <AccordionContainer>
          <ProviderHubSpecialty specialties={selectedHospital.specialtyCount} />
        </AccordionContainer>
      </div>
      {/* Second Column: Services (NPI type-2 organizations); individual practitioners live in the IAP drawer */}
      <div className="contractentity-profile__grid-column--scroll" ref={rightColumnRef}>
        <HospitalServicesTable
          hospitalServicesData={hospitalServicesData}
          pageIndex={pageIndex}
          pageSize={pageSize}
          resetToFirstPage={resetToFirstPage}
          setPageIndex={setPageIndex}
          setPageSize={setPageSize}
          setResetToFirstPage={setResetToFirstPage}
        />
      </div>
    </div>
  );
};
